import { webba } from './webba.js'

const BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

export interface ApiErrorBody {
  error?: string
  reason?: string
  message?: string
}

export class ApiError extends Error {
  readonly status: number
  readonly body: ApiErrorBody | string | null

  constructor(status: number, body: ApiErrorBody | string | null, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

// Listeners notified on 401 so AuthContext can clear local state and redirect.
type UnauthorizedListener = () => void
const unauthorizedListeners = new Set<UnauthorizedListener>()

export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener)
  return () => {
    unauthorizedListeners.delete(listener)
  }
}

// AuthContext registers its getValidAccessToken so the api wrapper can refresh
// transparently before each request instead of always sending a stale token.
type TokenProvider = () => Promise<string | null>
let tokenProvider: TokenProvider | null = null

export function setTokenProvider(provider: TokenProvider | null): void {
  tokenProvider = provider
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
  headers?: Record<string, string>
}

async function getAccessToken(): Promise<string | null> {
  if (tokenProvider) return tokenProvider()
  // Pre-Provider mount fallback: read whatever the SDK has cached.
  const stored = webba.getStoredTokens()
  return stored?.accessToken ?? null
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const token = await getAccessToken()

  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...opts.headers,
  }
  if (token) headers['Authorization'] = `Bearer ${token}`
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json'

  const init: RequestInit = {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'omit',
  }
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body)
  if (opts.signal) init.signal = opts.signal

  const response = await fetch(`${BASE_URL}${path}`, init)

  if (response.status === 401) {
    for (const listener of unauthorizedListeners) listener()
  }

  if (!response.ok) {
    const text = await response.text()
    let parsed: ApiErrorBody | string | null = null
    let message = `${response.status} ${response.statusText}`
    if (text) {
      try {
        parsed = JSON.parse(text) as ApiErrorBody
        message = parsed.reason || parsed.error || parsed.message || message
      } catch {
        parsed = text
        message = text || message
      }
    }
    throw new ApiError(response.status, parsed, message)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export const api = {
  get: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  delete: <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...opts, method: 'DELETE' }),
}

export interface MeResponse {
  id: string
  email: string
  name: string
  bootstrap: unknown
}

/**
 * A user-attached file as sent over the wire. `data` is the base64-encoded
 * payload WITHOUT the `data:<mime>;base64,` prefix — the backend reattaches
 * it when needed. Kept tiny on the client (5MB image / 1MB text caps) so
 * we don't blow JSON request bodies past sensible limits.
 */
export interface Attachment {
  name: string
  mime: string
  data: string
  size: number
}

export interface ChatRequest {
  message: string
  session_id?: string
  channel?: 'pwa'
  /** Force local LLM (private mode). Default uses Groq with auto-fallback. */
  local?: boolean
  attachments?: Attachment[]
}

export interface ChatToolCall {
  name: string
  arguments: Record<string, unknown>
  result?: unknown
}

export interface ChatResponse {
  session_id: string
  response: string
  tool_calls: ChatToolCall[]
  duration_ms: number
}

export async function chat(
  body: ChatRequest,
  opts?: { signal?: AbortSignal },
): Promise<ChatResponse> {
  return api.post<ChatResponse>(
    '/api/chat',
    { ...body, channel: body.channel ?? 'pwa' },
    opts?.signal ? { signal: opts.signal } : undefined,
  )
}

// ---- Streaming chat (SSE) ----------------------------------------------------------
// Mirrors the AgentEvent union from packages/core, plus 'session' and 'done'.
export type ChatStreamEvent =
  | { type: 'session'; session_id: string }
  | { type: 'turn_start'; traceId: string }
  | { type: 'recall_start' }
  | { type: 'recall_end'; recentCount: number; memoriesCount: number }
  | { type: 'llm_start'; iteration: number }
  | { type: 'llm_end'; iteration: number; toolCallCount: number; contentChars: number }
  | { type: 'llm_error'; iteration: number; message: string }
  | { type: 'tool_start'; name: string; args: unknown }
  | { type: 'tool_end'; name: string; ok: boolean; durationMs: number }
  | { type: 'turn_end'; durationMs: number; finalChars: number }
  | { type: 'done'; session_id: string; response: string; tool_calls: ChatToolCall[]; duration_ms: number }
  | { type: 'error'; message: string }

export interface ChatStreamOptions {
  signal?: AbortSignal
  onEvent: (event: ChatStreamEvent) => void
}

/**
 * Open an SSE stream against /api/chat-stream. Returns when the stream is
 * exhausted or the signal aborts. The caller drives all UI state via onEvent.
 */
export async function chatStream(body: ChatRequest, opts: ChatStreamOptions): Promise<void> {
  const tk = await getAccessToken()
  const res = await fetch(`${BASE_URL}/api/chat-stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(tk ? { Authorization: `Bearer ${tk}` } : {}),
    },
    body: JSON.stringify({ ...body, channel: body.channel ?? 'pwa' }),
    credentials: 'omit',
    ...(opts.signal ? { signal: opts.signal } : {}),
  })
  if (res.status === 401) {
    for (const listener of unauthorizedListeners) listener()
    throw new ApiError(401, null, 'unauthorized')
  }
  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => '')
    throw new ApiError(res.status, text || null, text || `${res.status} ${res.statusText}`)
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''
  // Each SSE event ends in a blank line. Within one event, we may receive
  // multiple `event:` and `data:` lines. We accumulate then dispatch on \n\n.
  let currentEvent = ''
  let currentData = ''

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })

    let nlIdx: number
    while ((nlIdx = buffer.indexOf('\n')) !== -1) {
      const line = buffer.slice(0, nlIdx).replace(/\r$/, '')
      buffer = buffer.slice(nlIdx + 1)

      if (line === '') {
        if (currentEvent && currentData) {
          try {
            const data = JSON.parse(currentData) as Record<string, unknown>
            console.info('[sse] <-', currentEvent, currentData.slice(0, 80))
            opts.onEvent({ ...data, type: currentEvent } as ChatStreamEvent)
          } catch (e) {
            console.warn('[sse] bad event payload:', e, currentData.slice(0, 200))
          }
        }
        currentEvent = ''
        currentData = ''
        continue
      }
      if (line.startsWith('event:')) {
        currentEvent = line.slice(6).trim()
      } else if (line.startsWith('data:')) {
        currentData += (currentData ? '\n' : '') + line.slice(5).trim()
      }
      // Other SSE fields (id:, retry:) ignored.
    }
  }
}


export interface VapidKeyResponse {
  key: string
}

export interface PushSubscribeBody {
  endpoint: string
  keys: { p256dh: string; auth: string }
  userAgent?: string
}

export interface PushTestResponse {
  delivered: number
  dropped: number
}

export async function getVapidPublicKey(): Promise<string> {
  const res = await api.get<VapidKeyResponse>('/api/web-push/vapid-public')
  return res.key
}

export async function subscribePush(body: PushSubscribeBody): Promise<void> {
  await api.post<{ ok: boolean }>('/api/web-push/subscribe', body)
}

export async function unsubscribePush(endpoint: string): Promise<void> {
  await api.post<{ ok: boolean }>('/api/web-push/unsubscribe', { endpoint })
}

export async function testPush(): Promise<PushTestResponse> {
  return api.post<PushTestResponse>('/api/web-push/test')
}

// ---- Memories ---------------------------------------------------------------------
export interface Memory {
  id: number
  type: string
  content: string
  confidence: number
  status: string
  created_at: string
  last_accessed_at: string
  access_count: number
}

export async function listMemories(opts: { q?: string; limit?: number } = {}): Promise<Memory[]> {
  const params = new URLSearchParams()
  if (opts.q) params.set('q', opts.q)
  if (opts.limit) params.set('limit', String(opts.limit))
  const qs = params.toString()
  const res = await api.get<{ memories: Memory[] }>(`/api/memories${qs ? `?${qs}` : ''}`)
  return res.memories
}

export async function deleteMemory(id: number): Promise<void> {
  await api.delete<{ ok: boolean }>(`/api/memories/${id}`)
}

// ---- Tasks ------------------------------------------------------------------------
export type TaskStatus = 'pending' | 'running' | 'done' | 'failed' | 'cancelled'
export type TaskType = 'constellation' | 'reminder' | 'mail' | 'search' | 'other'

export interface Task {
  id: string
  type: TaskType
  description: string
  status: TaskStatus
  payload: Record<string, unknown>
  due_at: string | null
  channel: string | null
  attempts: number
  last_error: string | null
  created_at: string
  completed_at: string | null
}

export async function listTasks(status?: TaskStatus): Promise<Task[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : ''
  const res = await api.get<{ tasks: Task[] }>(`/api/tasks${qs}`)
  return res.tasks
}

export async function cancelTask(id: string): Promise<void> {
  await api.post<{ ok: boolean }>(`/api/tasks/${id}/cancel`)
}

// ---- Sessions ---------------------------------------------------------------------
export interface SessionSummary {
  session_id: string
  started_at: string
  last_at: string
  message_count: number
  last_channel: string
  first_user_message: string | null
  /** LLM-generated short title (3-5 words). Null for legacy or freshly-created sessions. */
  title: string | null
  last_message: string | null
}

export interface SessionMessage {
  id: number
  role: 'user' | 'assistant' | 'system' | 'tool'
  content: string
  channel: string
  metadata: Record<string, unknown>
  created_at: string
}

export async function listSessions(limit = 50): Promise<SessionSummary[]> {
  const res = await api.get<{ sessions: SessionSummary[] }>(`/api/sessions?limit=${limit}`)
  return res.sessions
}

export async function getSessionMessages(sessionId: string): Promise<SessionMessage[]> {
  const res = await api.get<{ messages: SessionMessage[] }>(`/api/sessions/${sessionId}/messages`)
  return res.messages
}

export async function deleteSession(id: string): Promise<void> {
  await api.delete<{ ok: boolean }>(`/api/sessions/${id}`)
}

// ---- Server info --------------------------------------------------------------------
export interface ServerInfoResponse {
  llm: { provider: 'openai' | 'groq' | 'ollama'; model: string }
  voice: { url: string }
  version: string
  uptime_sec: number
}

export async function getServerInfo(): Promise<ServerInfoResponse> {
  return api.get<ServerInfoResponse>('/api/info')
}

export interface VoiceHealthResponse {
  ok: boolean
  service: string
  backend: 'local' | 'realtime'
  // local
  whisper_loaded?: boolean
  vad?: string
  kokoro_loaded?: boolean
  stt_model?: string
  tts_voice?: string
  // realtime
  model?: string
  voice?: string
  openai_key_set?: boolean
}

/** Same-origin GET on /voice/healthz, no auth needed. */
export async function getVoiceHealth(): Promise<VoiceHealthResponse> {
  const res = await fetch('/voice/healthz', { credentials: 'omit' })
  if (!res.ok) throw new Error(`voice/healthz HTTP ${res.status}`)
  return (await res.json()) as VoiceHealthResponse
}

// ---- Traces -------------------------------------------------------------------------
export type TraceKind = 'llm_call' | 'tool_call' | 'recall' | 'embedding'

export interface Trace {
  id: number
  trace_id: string
  span_kind: TraceKind
  name: string
  input: unknown
  output: unknown
  duration_ms: number | null
  error: string | null
  created_at: string
}

export async function listTraces(opts: { limit?: number; errorsOnly?: boolean } = {}): Promise<Trace[]> {
  const params = new URLSearchParams()
  if (opts.limit) params.set('limit', String(opts.limit))
  if (opts.errorsOnly) params.set('errors', '1')
  const qs = params.toString()
  const res = await api.get<{ traces: Trace[] }>(`/api/traces${qs ? `?${qs}` : ''}`)
  return res.traces
}

// ---- MCP / Connectors --------------------------------------------------------------
export interface McpServer {
  name: string
  status: 'connecting' | 'up' | 'down' | 'disabled'
  tools: string[]
  tool_count: number
  error?: string
  startedAt?: string
  description?: string
}

export interface McpTool {
  name: string         // prefixed: "filesystem__read_file"
  server: string
  original: string     // bare tool name
  description: string
}

export interface McpStatusResponse {
  servers: McpServer[]
  tools: McpTool[]
}

export async function getMcpStatus(): Promise<McpStatusResponse> {
  return api.get<McpStatusResponse>('/api/mcp')
}

// ---- OAuth (Google Calendar today) -------------------------------------------------
export interface GoogleOAuthStatus {
  connected: boolean
  email: string | null
  scopes: string[]
}

export async function getGoogleOAuthStatus(): Promise<GoogleOAuthStatus> {
  return api.get<GoogleOAuthStatus>('/api/oauth/google/status')
}

export async function startGoogleOAuth(): Promise<string> {
  const res = await api.post<{ auth_url: string }>('/api/oauth/google/start')
  return res.auth_url
}

export async function disconnectGoogleOAuth(): Promise<void> {
  await api.delete<{ ok: boolean }>('/api/oauth/google')
}

// ---- Stark Bridge (paired devices) -------------------------------------------------
export interface BridgeDevice {
  id: string
  label: string
  hostname: string | null
  os: string | null
  version: string | null
  capabilities: string[]
  created_at: string
  last_seen_at: string | null
}

export async function listBridges(): Promise<BridgeDevice[]> {
  const res = await api.get<{ bridges: BridgeDevice[] }>('/api/bridges')
  return res.bridges
}

export async function createBridgePairingCode(): Promise<{ code: string; expires_at: string }> {
  return api.post<{ code: string; expires_at: string }>('/api/bridges/pairing')
}

export async function revokeBridge(id: string): Promise<void> {
  await api.delete<{ ok: boolean }>(`/api/bridges/${id}`)
}

// ---- Audit (Sprint 17) -------------------------------------------------------------
export type ToolSafety = 'safe' | 'confirm' | 'destructive'

export interface AuditEvent {
  id: number
  tool: string
  safety: ToolSafety
  ok: boolean
  duration_ms: number | null
  error: string | null
  input: unknown
  output: unknown
  at: string
}

export interface ToolCatalogEntry {
  name: string
  safety: ToolSafety
  description: string
}

export async function listAuditEvents(limit = 50): Promise<AuditEvent[]> {
  const res = await api.get<{ events: AuditEvent[] }>(`/api/audit?limit=${limit}`)
  return res.events
}

export async function listToolCatalog(): Promise<ToolCatalogEntry[]> {
  const res = await api.get<{ tools: ToolCatalogEntry[] }>('/api/tools')
  return res.tools
}

// ---- Realtime SSE (Sprint 18 — wake word relay) -----------------------------------
export type RealtimeEvent =
  | { type: 'ready'; at: string }
  | { type: 'heartbeat' }
  | { type: 'wake'; bridgeId: string; userId: string; at: string }

export interface RealtimeSubscribeOpts {
  signal?: AbortSignal
  onEvent: (event: RealtimeEvent) => void
}

/**
 * Long-lived GET /api/realtime/events. EventSource can't carry a Bearer
 * token in headers so we use fetch + manual SSE parsing — same approach as
 * chatStream above. Caller passes an AbortSignal to tear down on unmount.
 */
export async function subscribeRealtimeEvents(opts: RealtimeSubscribeOpts): Promise<void> {
  const tk = await getAccessToken()
  const res = await fetch(`${BASE_URL}/api/realtime/events`, {
    method: 'GET',
    headers: {
      Accept: 'text/event-stream',
      ...(tk ? { Authorization: `Bearer ${tk}` } : {}),
    },
    credentials: 'omit',
    ...(opts.signal ? { signal: opts.signal } : {}),
  })
  if (res.status === 401) {
    for (const listener of unauthorizedListeners) listener()
    throw new ApiError(401, null, 'unauthorized')
  }
  if (!res.ok || !res.body) {
    throw new ApiError(res.status, null, `${res.status} ${res.statusText}`)
  }
  const reader = res.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''
  let currentEvent = ''
  let currentData = ''
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let nlIdx: number
    while ((nlIdx = buffer.indexOf('\n')) !== -1) {
      const line = buffer.slice(0, nlIdx).replace(/\r$/, '')
      buffer = buffer.slice(nlIdx + 1)
      if (line === '') {
        if (currentEvent && currentData) {
          try {
            const data = JSON.parse(currentData) as Record<string, unknown>
            opts.onEvent({ ...data, type: currentEvent } as RealtimeEvent)
          } catch { /* ignore malformed */ }
        }
        currentEvent = ''
        currentData = ''
        continue
      }
      if (line.startsWith('event:')) currentEvent = line.slice(6).trim()
      else if (line.startsWith('data:')) currentData += (currentData ? '\n' : '') + line.slice(5).trim()
    }
  }
}
